export function formatBytes(bytes: number | null): string {
  if (bytes == null || !Number.isFinite(bytes)) {
    return "Unknown size";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIx = 0;

  while (value >= 1024 && unitIx < units.length - 1) {
    value /= 1024;
    unitIx += 1;
  }

  return `${value.toFixed(unitIx === 0 ? 0 : 2)} ${units[unitIx]}`;
}

/**
 * "5 minutes ago" / "2 hours ago" / "in 3 days" from a timestamp (epoch ms,
 * ISO string, or Date), via Intl.RelativeTimeFormat at minute/hour/day
 * granularity. Returns null when the input can't be parsed.
 *
 * This is the verbose, localized form shared by the offline banner and the
 * dev build badge. The round activity log has its own compact formatter
 * (`formatRelativeTime` in Logs.tsx: "2h ago", year/month granularity) tuned
 * for its dense table — deliberately not merged here.
 */
export function formatRelativeTimeAgo(
  input: number | string | Date,
): string | null {
  const ms =
    input instanceof Date
      ? input.getTime()
      : typeof input === "number"
        ? input
        : new Date(input).getTime();

  if (Number.isNaN(ms)) {
    return null;
  }

  const diffMs = ms - Date.now();
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const minutes = Math.round(diffMs / 60_000);

  if (Math.abs(minutes) < 60) {
    return rtf.format(minutes, "minute");
  }

  const hours = Math.round(diffMs / 3_600_000);

  if (Math.abs(hours) < 24) {
    return rtf.format(hours, "hour");
  }

  return rtf.format(Math.round(diffMs / 86_400_000), "day");
}

export function formatDuration(durationSeconds: number | null): string | null {
  if (durationSeconds === null) {
    return null;
  }

  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor(durationSeconds / 60) % 60;
  const seconds = Math.floor(durationSeconds % 60);

  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

// Rounded, human-readable length — "2 hr 19 min", "38 min", "under a minute".
// For scanning surfaces (the queue drawer) where the reader wants a sense of
// how long something is, not the frame-accurate H:MM:SS formatDuration gives.
// Minutes are rounded rather than truncated so a 13:38 remainder reads as
// "14 min" instead of the pessimistic "13 min".
export function formatDurationApprox(
  durationSeconds: number | null,
): string | null {
  if (durationSeconds === null) {
    return null;
  }

  const totalMinutes = Math.round(Math.max(0, durationSeconds) / 60);

  if (totalMinutes < 1) {
    return "under a minute";
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }

  if (minutes === 0) {
    return `${hours} hr`;
  }

  return `${hours} hr ${minutes} min`;
}
